import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Avatar,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTeacher } from '../../context/TeacherContext';
import apiAxiosInstance from '../../utils/axiosInstance';
import { Teacher } from '../../Types/Teacher';
import { Standard } from '../../Types/Standard';
import { SUBJECTS, DEFAULT_PRIMARY_SUBJECT } from '../../config/subjects';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { teacher, setTeacher } = useTeacher();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gradeLevel: '',
    teachingStandards: '',
    primarySubject: DEFAULT_PRIMARY_SUBJECT,
    teachingSubjects: [] as string[],
  });

  // Initialize form data from teacher context
  useEffect(() => {
    if (teacher) {
      const standardId = teacher.teachingStandards?.[0] ?? '';
      setFormData({
        firstName: teacher.firstName ?? '',
        lastName: teacher.lastName ?? '',
        email: teacher.email ?? '',
        gradeLevel: teacher.gradeLevel ?? '',
        teachingStandards: typeof standardId === 'string' ? standardId : standardId._id ?? '',
        primarySubject: teacher.primarySubject ?? DEFAULT_PRIMARY_SUBJECT,
        teachingSubjects: teacher.teachingSubjects?.map(ts => ts.subject).filter(Boolean) ?? [],
      });
    }
  }, [teacher]);

  // Fetch standards
  useEffect(() => {
    const fetchStandards = async () => {
      try {
        const response = await apiAxiosInstance.get('/api/standards');
        setStandards(response.data);
      } catch (err) {
        console.error('Error fetching standards:', err);
      }
    };

    fetchStandards();
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleEdit = () => {
    setEditing(true);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleCancel = () => {
    setEditing(false);
    setErrorMessage(null);
    setSuccessMessage(null);
    // Reset form data to original values
    if (teacher) {
      const standardId = teacher.teachingStandards?.[0] ?? '';
      setFormData({
        firstName: teacher.firstName ?? '',
        lastName: teacher.lastName ?? '',
        email: teacher.email ?? '',
        gradeLevel: teacher.gradeLevel ?? '',
        teachingStandards: typeof standardId === 'string' ? standardId : standardId._id ?? '',
        primarySubject: teacher.primarySubject ?? DEFAULT_PRIMARY_SUBJECT,
        teachingSubjects: teacher.teachingSubjects?.map(ts => ts.subject).filter(Boolean) ?? [],
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      if (!teacher?._id) {
        throw new Error('No teacher ID available');
      }

              const response = await apiAxiosInstance.patch(`/auth/complete-profile/${teacher._id}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gradeLevel: formData.gradeLevel,
        teachingStandards: formData.teachingStandards,
        primarySubject: formData.primarySubject,
        teachingSubjects: formData.teachingSubjects,
      });

      if (response.data) {
        setTeacher(response.data);
        setEditing(false);
        setSuccessMessage('Profile updated successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error('Profile update error:', err);
      setErrorMessage(err.response?.data?.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (teacher: Teacher): string => {
    if (teacher?.firstName && teacher?.lastName) {
      return `${teacher.firstName[0]}${teacher.lastName[0]}`.toUpperCase();
    }
    return teacher?.email?.[0].toUpperCase() || 'U';
  };

  const getSelectedStandardName = () => {
    const selectedStandard = standards.find(s => s._id === formData.teachingStandards);
    return selectedStandard?.name || 'Not selected';
  };

  if (!teacher) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}
            >
              {teacher.profilePicture ? (
                <img src={teacher.profilePicture} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                getInitials(teacher)
              )}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {teacher.firstName && teacher.lastName 
                  ? `${teacher.firstName} ${teacher.lastName}`
                  : 'Your Profile'
                }
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {teacher.email}
              </Typography>
            </Box>
          </Box>
          {!editing && (
            <IconButton 
              onClick={handleEdit}
              color="primary"
              sx={{ 
                bgcolor: 'primary.50',
                '&:hover': { bgcolor: 'primary.100' }
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Profile Content */}
        {editing ? (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleTextChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleTextChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Grade Level</InputLabel>
                  <Select
                    value={formData.gradeLevel}
                    label="Grade Level"
                    name="gradeLevel"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="K">Kindergarten</MenuItem>
                    <MenuItem value="1">1st Grade</MenuItem>
                    <MenuItem value="2">2nd Grade</MenuItem>
                    <MenuItem value="3">3rd Grade</MenuItem>
                    <MenuItem value="4">4th Grade</MenuItem>
                    <MenuItem value="5">5th Grade</MenuItem>
                    <MenuItem value="6">6th Grade</MenuItem>
                    <MenuItem value="7">7th Grade</MenuItem>
                    <MenuItem value="8">8th Grade</MenuItem>
                    <MenuItem value="9">9th Grade</MenuItem>
                    <MenuItem value="10">10th Grade</MenuItem>
                    <MenuItem value="11">11th Grade</MenuItem>
                    <MenuItem value="12">12th Grade</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Primary Subject</InputLabel>
                  <Select
                    value={formData.primarySubject}
                    label="Primary Subject"
                    name="primarySubject"
                    onChange={handleSelectChange}
                  >
                    {SUBJECTS.map((subject) => (
                      <MenuItem key={subject.value} value={subject.value}>
                        {subject.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Other Subjects</InputLabel>
                  <Select
                    multiple
                    value={formData.teachingSubjects}
                    label="Other Subjects"
                    name="teachingSubjects"
                    onChange={handleMultiSelectChange}
                    input={<OutlinedInput label="Other Subjects" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const subject = SUBJECTS.find(s => s.value === value);
                          return <Chip key={value} label={subject?.label || value} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {SUBJECTS.filter(s => s.value !== formData.primarySubject).map((subject) => (
                      <MenuItem key={subject.value} value={subject.value}>
                        <Checkbox checked={formData.teachingSubjects.indexOf(subject.value) > -1} />
                        <ListItemText primary={subject.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Teaching Standards</InputLabel>
                  <Select
                    value={formData.teachingStandards}
                    label="Teaching Standards"
                    name="teachingStandards"
                    onChange={handleSelectChange}
                  >
                    {standards.map((standard) => (
                      <MenuItem key={standard._id} value={standard._id}>
                        {standard.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          </form>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Personal Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">First Name</Typography>
                      <Typography variant="body1">{teacher.firstName || 'Not provided'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Last Name</Typography>
                      <Typography variant="body1">{teacher.lastName || 'Not provided'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{teacher.email}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Teaching Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Grade Level</Typography>
                      <Typography variant="body1">
                        {teacher.gradeLevel ? `${teacher.gradeLevel}${teacher.gradeLevel === 'K' ? 'indergarten' : teacher.gradeLevel === '1' ? 'st Grade' : teacher.gradeLevel === '2' ? 'nd Grade' : teacher.gradeLevel === '3' ? 'rd Grade' : 'th Grade'}` : 'Not provided'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Primary Subject</Typography>
                      <Typography variant="body1">
                        {SUBJECTS.find(s => s.value === teacher.primarySubject)?.label || teacher.primarySubject || 'Not provided'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Other Subjects</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {teacher.teachingSubjects && teacher.teachingSubjects.length > 0 ? (
                          teacher.teachingSubjects.map((ts) => {
                            const subject = SUBJECTS.find(s => s.value === ts.subject);
                            return (
                              <Chip 
                                key={ts.subject} 
                                label={subject?.label || ts.subject} 
                                size="small" 
                                variant="outlined"
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body1" color="text.secondary">None</Typography>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Teaching Standards</Typography>
                      <Typography variant="body1">
                        {getSelectedStandardName()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default Profile; 